/**
 * LocalizedLink - A Link component that automatically preserves the current locale
 * Use this instead of react-router-dom's Link to ensure language is maintained on navigation
 */

import type { ComponentProps } from "react";
import { forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";

import { addLocaleToPath, getLocaleFromPath } from "../utils/url-locales";

type LocalizedLinkProps = ComponentProps<typeof Link>;

export const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  ({ to, children, ...props }, ref) => {
    const location = useLocation();
    const currentLocale = getLocaleFromPath(location.pathname);

    // If the path is a string, add locale prefix
    const localizedTo = typeof to === "string" && currentLocale 
      ? addLocaleToPath(to, currentLocale) 
      : to;

    return (
      <Link ref={ref} to={localizedTo} {...props}>
        {children}
      </Link>
    );
  }
);

LocalizedLink.displayName = "LocalizedLink";

// Also export a utility hook for edge cases
export { useLocalizedNavigation } from "../hooks/useLocalizedNavigation";
